/**
 * One vocabulary of failures across every provider, so the node can offer the
 * right way out (add a key, top up, start Ollama) whoever wrote the shader.
 *
 * Messages never carry the user's key: provider text is passed through
 * `redactSecrets` before it becomes an error.
 */

import { ShaderGenerationError } from '../types'
import type { ShaderGenerationErrorCode } from '../types'

export function abortedError(): ShaderGenerationError {
  return new ShaderGenerationError('Generation was cancelled.', 'ABORTED')
}

export function isAbortError(error: unknown): boolean {
  return (
    (error instanceof ShaderGenerationError && error.code === 'ABORTED') ||
    (typeof error === 'object' && error !== null && 'name' in error && (error as { name: unknown }).name === 'AbortError')
  )
}

const TOKEN_LIKE = /\b(sk-[\w-]{8,}|xai-[\w-]{8,}|Bearer\s+[\w.-]{8,})/g

/** Removes the given secrets, and anything shaped like an API key, from provider text. */
export function redactSecrets(message: string, secrets: ReadonlyArray<string | undefined>): string {
  let clean = message
  for (const secret of secrets) {
    if (secret && secret.length >= 4) clean = clean.split(secret).join('[key]')
  }
  return clean.replace(TOKEN_LIKE, '[key]')
}

/** Best-effort message and code from an error response body (JSON or text). */
export async function readErrorBody(response: Response): Promise<{ message: string; code?: string }> {
  let text = ''
  try {
    text = await response.text()
  } catch {
    return { message: '' }
  }
  try {
    const body = JSON.parse(text) as { error?: unknown; message?: unknown; code?: unknown }
    const error = body.error
    const message =
      typeof error === 'string'
        ? error
        : typeof error === 'object' && error !== null && typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : typeof body.message === 'string'
            ? body.message
            : ''
    return { message, code: typeof body.code === 'string' ? body.code : undefined }
  } catch {
    return { message: text.slice(0, 300) }
  }
}

/**
 * Maps an HTTP failure to a generation error; `label` names the provider in the
 * message. A 402 from a provider is the user's own account there, not Genie
 * credits, so it stays `UNKNOWN`; the credits transport maps its own 402.
 */
export async function httpError(response: Response, label: string, secrets: ReadonlyArray<string | undefined> = []): Promise<ShaderGenerationError> {
  const body = await readErrorBody(response)
  const detail = redactSecrets(body.message, secrets).trim()
  const status = response.status
  // xAI answers a wrong key with 400 "Incorrect API key provided".
  const keyRejected = status === 400 && /api key|unauthori[sz]ed|authentication/i.test(detail)
  const code: ShaderGenerationErrorCode = status === 401 || status === 403 || keyRejected ? 'AUTH' : status === 429 ? 'RATE_LIMITED' : 'UNKNOWN'
  return new ShaderGenerationError(detail ? `${label}: ${detail}` : fallbackMessage(label, status), code)
}

function fallbackMessage(label: string, status: number): string {
  if (status === 401 || status === 403) return `${label} rejected the request. Check your API key.`
  if (status === 402) return `${label} says the account has no credit left.`
  if (status === 429) return `${label} is rate limiting requests. Wait a moment and try again.`
  return `${label} returned an error (HTTP ${status}).`
}

/**
 * Normalises anything a transport threw. `onNetworkError` turns a fetch
 * `TypeError` (unreachable host or CORS, the browser does not say which) into
 * a provider-specific explanation.
 */
export function toShaderError(
  error: unknown,
  options: { secrets?: ReadonlyArray<string | undefined>; onNetworkError?: () => ShaderGenerationError } = {},
): ShaderGenerationError {
  if (isAbortError(error)) return abortedError()
  if (error instanceof ShaderGenerationError) {
    const message = redactSecrets(error.message, options.secrets ?? [])
    return message === error.message ? error : new ShaderGenerationError(message, error.code)
  }
  if (error instanceof TypeError && options.onNetworkError) return options.onNetworkError()
  const message = error instanceof Error ? error.message : String(error)
  // A host (the desktop app) cannot construct this bundle's error class, so it
  // tags a plain Error with one of our codes instead.
  const tagged = typeof error === 'object' && error !== null ? (error as { code?: unknown }).code : undefined
  const code: ShaderGenerationErrorCode = isShaderGenerationErrorCode(tagged) ? tagged : error instanceof TypeError ? 'NETWORK' : 'UNKNOWN'
  return new ShaderGenerationError(redactSecrets(message || 'Generation failed.', options.secrets ?? []), code)
}

const ERROR_CODES: ReadonlySet<string> = new Set<ShaderGenerationErrorCode>([
  'PRICING_NOT_FOUND', 'INSUFFICIENT_BALANCE', 'AUTH', 'CORS', 'NETWORK', 'RATE_LIMITED',
  'VALIDATION', 'EMPTY', 'TRUNCATED', 'ABORTED', 'UNKNOWN',
])

function isShaderGenerationErrorCode(value: unknown): value is ShaderGenerationErrorCode {
  return typeof value === 'string' && ERROR_CODES.has(value)
}
